import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostListener, Input, Output } from '@angular/core';

type ExchangeValue = 'send' | 'receive' | null;

@Component({
    selector: 'app-exchanger-switch',
    templateUrl: './exchanger-switch.component.html',
    styleUrls: ['./exchanger-switch.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ExchangerSwitchComponent {
  constructor(private cdr: ChangeDetectorRef) {}
  @Input() label = 'Images';
  @Input() icon = 'image';
  @Input() sendLabel = 'Send';
  @Input() receiveLabel = 'Receive';
  @Input() disabled = false;

  private _value: ExchangeValue = null;
  dropdownOpen = false;
  selectedOption: string | null = null;

  readonly options = [
    { value: 'Images', icon: 'image', label: 'Images' },
    { value: 'Documents', icon: 'description', label: 'Documents' }
  ];

  @Input()
  set value(val: ExchangeValue) {
    if (this._value !== val) {
      this._value = val;
      this.cdr.markForCheck();
    }
  }

  get value(): ExchangeValue {
    return this._value;
  }

  @Output() valueChange = new EventEmitter<ExchangeValue>();
  @Output() optionChange = new EventEmitter<string>();

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.exchanger-switch__dropdown') && !this.shouldForceDropdownOpen()) {
      if (this.dropdownOpen) {
        this.dropdownOpen = false;
        this.cdr.markForCheck();
      }
    }
  }

  selectOption(option: string): void {
    if (this.disabled) {
      return;
    }
    this.selectedOption = option;
    this.dropdownOpen = false;
    this.optionChange.emit(option);
    
    // Reset SEND/RECEIVE toggle to null when an option is selected
    if (this._value !== null) {
      this._value = null;
      this.valueChange.emit(null);
    }
    
    this.cdr.markForCheck();
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    if (this.shouldForceDropdownOpen()) {
      this.dropdownOpen = true;
    } else {
      this.dropdownOpen = !this.dropdownOpen;
    }
    this.cdr.markForCheck();
  }

  setMode(mode: 'send' | 'receive'): void {
    if (this.isToggleDisabled() || this._value === mode) {
      return;
    }

    this._value = mode;
    this.valueChange.emit(this._value);
    this.cdr.markForCheck();
  }

  isActive(mode: 'send' | 'receive'): boolean {
    return this._value === mode;
  }

  hasValue(): boolean {
    return this._value !== null;
  }

  isSelected(option: string): boolean {
    return this.selectedOption === option;
  }

  getSelectedOptionIcon(): string | null {
    if (!this.selectedOption) {
      return null;
    }
    const option = this.options.find(opt => opt.value === this.selectedOption);
    return option ? option.icon : null;
  }

  getSelectedOptionLabel(): string {
    if (!this.selectedOption) {
      return 'Choose option';
    }
    const option = this.options.find(opt => opt.value === this.selectedOption);
    return option ? option.label : this.selectedOption;
  }

  isDocumentsTheme(): boolean {
    return this.selectedOption === 'Documents';
  }

  isImagesTheme(): boolean {
    return this.selectedOption === 'Images' || !this.selectedOption;
  }

  getInstructionText(): string {
    if (this.selectedOption) {
      return 'Now choose what action you want to take';
    }
    return 'Please first select what you want to exchange';
  }

  isToggleDisabled(): boolean {
    return this.disabled || !this.selectedOption;
  }

  shouldForceDropdownOpen(): boolean {
    return !this.selectedOption;
  }

  isDropdownMenuVisible(): boolean {
    return this.shouldForceDropdownOpen() || this.dropdownOpen;
  }
}

